import { useCallback, useState, useDebugValue, useEffect, useMemo } from "react";
import { LOCAL_STORAGE_CHANGE_EVENT } from "../../../../constants/constants/events.js";
import { isBrowser, tryParse, isFunction, useEffectEvent, isLocalStorageChanged } from "./utils.js";
function writeToStorage(key, value) {
  if (!isBrowser()) {
    return;
  }
  try {
    globalThis.localStorage.setItem(key, JSON.stringify(value));
    globalThis.dispatchEvent(
      new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
        detail: { key }
      })
    );
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("circular structure")) {
      throw new TypeError(
        "The object that was given to the writeToStorage function has circular references.\nFor more information, check here: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Cyclic_object_value"
      );
    }
    throw error;
  }
}
function deleteFromStorage(key) {
  if (!isBrowser()) {
    return;
  }
  globalThis.localStorage.removeItem(key);
  globalThis.dispatchEvent(
    new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
      detail: { key, value: null }
    })
  );
}
function useLocalStorage(key, initialValue) {
  const readValue = useCallback(() => {
    if (!isBrowser()) {
      return initialValue;
    }
    try {
      if (globalThis.localStorage.hasOwnProperty(key)) {
        const item = globalThis.localStorage.getItem(key);
        if (item !== null) {
          return tryParse(item);
        }
      }
    } catch (error) {
      console.warn(error);
    }
    return initialValue;
  }, [key, initialValue]);
  const [localState, setLocalState] = useState(readValue);
  useDebugValue(localState, (v) => ({ key, value: v }));
  const handleSetState = useCallback(
    (action) => {
      try {
        const nextState = isFunction(action) ? action(localState) : action;
        writeToStorage(key, nextState);
      } catch (error) {
        console.warn(error);
      }
    },
    [key, localState]
  );
  const onStorageChange = useEffectEvent((event) => {
    if (isLocalStorageChanged(event)) {
      if (event.detail.key === key) {
        setLocalState(readValue());
      }
    } else {
      if (event.key === key) {
        setLocalState(readValue());
      }
    }
  });
  useEffect(() => {
    if (!isBrowser()) {
      return;
    }
    globalThis.addEventListener("storage", onStorageChange);
    globalThis.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, onStorageChange);
    return () => {
      globalThis.removeEventListener("storage", onStorageChange);
      globalThis.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, onStorageChange);
    };
  }, []);
  const removeFromStorage = useCallback(() => {
    deleteFromStorage(key);
  }, [key]);
  return useMemo(() => {
    return [localState, handleSetState, removeFromStorage];
  }, [localState, handleSetState, removeFromStorage]);
}
export {
  useLocalStorage
};
//# sourceMappingURL=use-local-storage.js.map
